<template>
  <v-card>
    <v-card-title class="bg-purple text-white d-flex justify-space-between align-center">
      <span>
        <v-icon class="mr-2">mdi-key</v-icon>
        Permissions: {{ roleName }}
      </span>
      <v-btn icon variant="text" @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pt-4">
      <v-progress-linear v-if="loading" indeterminate color="purple"></v-progress-linear>

      <div v-else>
        <!-- UI Sections -->
        <v-card variant="outlined" class="mb-4">
          <v-card-title class="bg-blue-lighten-5 py-2 d-flex justify-space-between align-center">
            <span>
              <v-icon class="mr-2" size="small">mdi-view-dashboard</v-icon>
              UI Sections
            </span>
            <div>
              <v-btn size="x-small" variant="text" @click="selectAllInCategory('ui_section')">All</v-btn>
              <v-btn size="x-small" variant="text" @click="deselectAllInCategory('ui_section')">None</v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col v-for="perm in permissions.ui_sections" :key="perm.id" cols="6" sm="4" md="3">
                <v-checkbox
                  v-model="selectedPermissions"
                  :value="perm.id"
                  :label="perm.display_name"
                  density="compact"
                  hide-details
                  color="blue"
                >
                  <template v-slot:label>
                    <span class="text-body-2">{{ perm.display_name }}</span>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Entity CRUD -->
        <v-card variant="outlined" class="mb-4">
          <v-card-title class="bg-green-lighten-5 py-2 d-flex justify-space-between align-center">
            <span>
              <v-icon class="mr-2" size="small">mdi-database</v-icon>
              Entity Permissions (CRUD)
            </span>
            <div>
              <v-btn size="x-small" variant="text" @click="selectAllInCategory('entity_crud')">All</v-btn>
              <v-btn size="x-small" variant="text" @click="deselectAllInCategory('entity_crud')">None</v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <v-expansion-panels variant="accordion">
              <v-expansion-panel
                v-for="(entityPerms, entityName) in permissions.entity_crud"
                :key="entityName"
              >
                <v-expansion-panel-title>
                  <div class="d-flex align-center justify-space-between w-100 mr-4">
                    <span class="font-weight-medium text-capitalize">{{ entityName.replace('_', ' ') }}</span>
                    <v-chip size="x-small" :color="getEntitySelectedCount(entityName) > 0 ? 'success' : 'grey'">
                      {{ getEntitySelectedCount(entityName) }}/{{ entityPerms.length }}
                    </v-chip>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-row>
                    <v-col v-for="perm in entityPerms" :key="perm.id" cols="6" sm="3">
                      <v-checkbox
                        v-model="selectedPermissions"
                        :value="perm.id"
                        :label="perm.action"
                        density="compact"
                        hide-details
                        :color="getActionColor(perm.action)"
                      >
                        <template v-slot:label>
                          <v-icon size="small" class="mr-1" :color="getActionColor(perm.action)">
                            {{ getActionIcon(perm.action) }}
                          </v-icon>
                          <span class="text-body-2 text-capitalize">{{ perm.action }}</span>
                        </template>
                      </v-checkbox>
                    </v-col>
                  </v-row>
                  <div class="mt-2">
                    <v-btn size="x-small" variant="text" @click="selectAllForEntity(entityName)">Select All</v-btn>
                    <v-btn size="x-small" variant="text" @click="deselectAllForEntity(entityName)">Deselect All</v-btn>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>

        <!-- Features -->
        <v-card variant="outlined" class="mb-4">
          <v-card-title class="bg-orange-lighten-5 py-2 d-flex justify-space-between align-center">
            <span>
              <v-icon class="mr-2" size="small">mdi-cog</v-icon>
              Features
            </span>
            <div>
              <v-btn size="x-small" variant="text" @click="selectAllInCategory('feature')">All</v-btn>
              <v-btn size="x-small" variant="text" @click="deselectAllInCategory('feature')">None</v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col v-for="perm in permissions.features" :key="perm.id" cols="12" sm="6" md="4">
                <v-checkbox
                  v-model="selectedPermissions"
                  :value="perm.id"
                  density="compact"
                  hide-details
                  color="orange"
                >
                  <template v-slot:label>
                    <div>
                      <span class="text-body-2">{{ perm.display_name }}</span>
                      <div class="text-caption text-grey">{{ perm.description }}</div>
                    </div>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Summary -->
        <v-alert type="info" density="compact" class="mb-4">
          <strong>{{ selectedPermissions.length }}</strong> permissions selected
        </v-alert>
      </div>
    </v-card-text>

    <v-card-actions>
      <v-btn variant="text" @click="$emit('close')">Cancel</v-btn>
      <v-spacer></v-spacer>
      <v-btn color="purple" variant="flat" @click="savePermissions" :loading="saving">
        <v-icon class="mr-2">mdi-content-save</v-icon>
        Save Permissions
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { defineComponent } from 'vue'
import { getPermissionsList, getRolePermissions, updateRolePermissions } from '@/composables/usePermissionsAPI'
import { log } from '@/composables/useLog'

export default defineComponent({
  name: 'RolePermissionsEditor',
  props: {
    roleId: {
      type: Number,
      required: true
    },
    roleName: {
      type: String,
      default: ''
    },
    token: {
      type: String,
      required: true
    }
  },
  emits: ['close', 'saved'],
  data() {
    return {
      loading: false,
      saving: false,
      permissions: {
        ui_sections: [],
        entity_crud: {},
        features: []
      },
      selectedPermissions: []
    }
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        // Load all permissions
        const permResult = await getPermissionsList(this.token)
        if (!permResult.error) {
          this.permissions = permResult.permissions
        }

        // Load role's current permissions
        const roleResult = await getRolePermissions(this.token, this.roleId)
        if (!roleResult.error) {
          this.selectedPermissions = roleResult.permission_ids || []
        }
      } catch (err) {
        log('Error loading permissions:', err)
      } finally {
        this.loading = false
      }
    },

    async savePermissions() {
      this.saving = true
      try {
        const result = await updateRolePermissions(this.token, this.roleId, this.selectedPermissions)
        if (!result.error) {
          this.$emit('saved', result)
        } else {
          alert(result.message || 'Error saving permissions')
        }
      } catch (err) {
        log('Error saving permissions:', err)
        alert('Error saving permissions')
      } finally {
        this.saving = false
      }
    },

    selectAllInCategory(category) {
      let perms = []
      if (category === 'ui_section') {
        perms = this.permissions.ui_sections
      } else if (category === 'entity_crud') {
        perms = Object.values(this.permissions.entity_crud).flat()
      } else if (category === 'feature') {
        perms = this.permissions.features
      }

      perms.forEach(p => {
        if (!this.selectedPermissions.includes(p.id)) {
          this.selectedPermissions.push(p.id)
        }
      })
    },

    deselectAllInCategory(category) {
      let permIds = []
      if (category === 'ui_section') {
        permIds = this.permissions.ui_sections.map(p => p.id)
      } else if (category === 'entity_crud') {
        permIds = Object.values(this.permissions.entity_crud).flat().map(p => p.id)
      } else if (category === 'feature') {
        permIds = this.permissions.features.map(p => p.id)
      }

      this.selectedPermissions = this.selectedPermissions.filter(id => !permIds.includes(id))
    },

    selectAllForEntity(entityName) {
      const perms = this.permissions.entity_crud[entityName] || []
      perms.forEach(p => {
        if (!this.selectedPermissions.includes(p.id)) {
          this.selectedPermissions.push(p.id)
        }
      })
    },

    deselectAllForEntity(entityName) {
      const permIds = (this.permissions.entity_crud[entityName] || []).map(p => p.id)
      this.selectedPermissions = this.selectedPermissions.filter(id => !permIds.includes(id))
    },

    getEntitySelectedCount(entityName) {
      const permIds = (this.permissions.entity_crud[entityName] || []).map(p => p.id)
      return this.selectedPermissions.filter(id => permIds.includes(id)).length
    },

    getActionIcon(action) {
      const icons = {
        view: 'mdi-eye',
        create: 'mdi-plus',
        edit: 'mdi-pencil',
        delete: 'mdi-delete'
      }
      return icons[action] || 'mdi-check'
    },

    getActionColor(action) {
      const colors = {
        view: 'blue',
        create: 'green',
        edit: 'orange',
        delete: 'red'
      }
      return colors[action] || 'grey'
    }
  },
  mounted() {
    this.loadData()
  }
})
</script>

<style scoped>
.w-100 {
  width: 100%;
}
</style>
